import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminCustomers.css";

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDateShort(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function initials(first, last) {
  return `${(first || "?")[0]}${(last || "?")[0]}`.toUpperCase();
}

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onResetPw, onToggle, onDelete, isActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="acu-dot-menu" ref={ref}>
      <button
        className="acu-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="acu-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="acu-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="acu-dot-menu__icon">✏</span> Edit
          </button>
          <button onClick={() => { onResetPw(); setOpen(false); }}>
            <span className="acu-dot-menu__icon">🔑</span> Reset Password
          </button>
          <button onClick={() => { onToggle(); setOpen(false); }}>
            <span className="acu-dot-menu__icon">{isActive ? "🚫" : "✅"}</span>
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button
            className="acu-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="acu-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Badges
   ───────────────────────────────── */
function ActiveBadge({ active }) {
  const on = Number(active) === 1;
  return (
    <span className={`acu-badge ${on ? "acu-badge--active" : "acu-badge--inactive"}`}>
      {on ? "Active" : "Inactive"}
    </span>
  );
}

function VerifiedBadge({ verified }) {
  const on = Number(verified) === 1;
  return (
    <span className={`acu-badge ${on ? "acu-badge--verified" : "acu-badge--unverified"}`}>
      {on ? "✓ Verified" : "Unverified"}
    </span>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ customer, onClose, onEdit }) {
  return (
    <div className="acu-modal-overlay" onClick={onClose}>
      <div className="acu-modal acu-modal--view" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="acu-modal__header">
          <div className="acu-modal__title-row">
            <span className="acu-modal__tag">CUSTOMER</span>
            <h2 className="acu-modal__title">Profile</h2>
          </div>
          <button className="acu-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="acu-view__body">

          {/* Avatar + identity */}
          <div className="acu-view__hero">
            <div className="acu-avatar acu-avatar--lg">
              {initials(customer.first_name, customer.last_name)}
            </div>
            <div className="acu-view__hero-info">
              <h3 className="acu-view__full-name">
                {customer.first_name} {customer.last_name}
              </h3>
              <a href={`mailto:${customer.email}`} className="acu-view__email">
                {customer.email}
              </a>
              {customer.phone && (
                <a href={`tel:${customer.phone}`} className="acu-view__phone">
                  {customer.phone}
                </a>
              )}
              <div className="acu-view__badges">
                <ActiveBadge active={customer.is_active} />
                <VerifiedBadge verified={customer.email_verified} />
              </div>
            </div>
          </div>

          {/* Detail grid */}
          <div className="acu-view__sections">

            {/* Account */}
            <div className="acu-view__section">
              <h4 className="acu-view__section-title">Account</h4>
              <div className="acu-view__grid">
                <div className="acu-view__field">
                  <span className="acu-view__label">Customer ID</span>
                  <span className="acu-view__value">#{customer.id}</span>
                </div>
                <div className="acu-view__field">
                  <span className="acu-view__label">Registered</span>
                  <span className="acu-view__value">{fmtDateShort(customer.created_at)}</span>
                </div>
                <div className="acu-view__field">
                  <span className="acu-view__label">Last Login</span>
                  <span className="acu-view__value">{fmtDate(customer.last_login)}</span>
                </div>
                <div className="acu-view__field">
                  <span className="acu-view__label">Orders</span>
                  <span className="acu-view__value">{customer.order_count ?? "—"}</span>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="acu-view__section">
              <h4 className="acu-view__section-title">Shipping Address</h4>
              {customer.address || customer.city || customer.province ? (
                <div className="acu-view__grid">
                  {customer.address && (
                    <div className="acu-view__field acu-view__field--full">
                      <span className="acu-view__label">Street Address</span>
                      <span className="acu-view__value">{customer.address}</span>
                    </div>
                  )}
                  {customer.city && (
                    <div className="acu-view__field">
                      <span className="acu-view__label">City</span>
                      <span className="acu-view__value">{customer.city}</span>
                    </div>
                  )}
                  {customer.province && (
                    <div className="acu-view__field">
                      <span className="acu-view__label">Province</span>
                      <span className="acu-view__value">{customer.province}</span>
                    </div>
                  )}
                  {customer.postal_code && (
                    <div className="acu-view__field">
                      <span className="acu-view__label">Postal Code</span>
                      <span className="acu-view__value">{customer.postal_code}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="acu-view__no-address">No address on file.</p>
              )}
            </div>

          </div>
        </div>

        <div className="acu-modal__footer">
          <button className="acu-btn acu-btn--cancel" onClick={onClose}>Close</button>
          <button className="acu-btn acu-btn--save" onClick={() => { onClose(); onEdit(); }}>
            ✏ Edit Customer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   EDIT MODAL
   ═══════════════════════════════════ */
const EMPTY_EDIT = {
  first_name:  "",
  last_name:   "",
  email:       "",
  phone:       "",
  address:     "",
  city:        "",
  province:    "",
  postal_code: "",
  is_active:   1,
  email_verified: 0,
};

function EditModal({ customer, onClose, onSaved }) {
  const [form,   setForm]   = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (customer) {
      setForm({
        first_name:     customer.first_name     || "",
        last_name:      customer.last_name      || "",
        email:          customer.email          || "",
        phone:          customer.phone          || "",
        address:        customer.address        || "",
        city:           customer.city           || "",
        province:       customer.province       || "",
        postal_code:    customer.postal_code    || "",
        is_active:      Number(customer.is_active),
        email_verified: Number(customer.email_verified),
      });
    }
  }, [customer]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    setError("");
    if (!form.first_name.trim()) return setError("First name is required.");
    if (!form.last_name.trim())  return setError("Last name is required.");
    if (!form.email.trim())      return setError("Email address is required.");

    setSaving(true);
    try {
      await apiFetch(`/api/admin/customers/${customer.id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          first_name:     form.first_name.trim(),
          last_name:      form.last_name.trim(),
          email:          form.email.trim(),
          phone:          form.phone.trim()       || null,
          address:        form.address.trim()     || null,
          city:           form.city.trim()        || null,
          province:       form.province.trim()    || null,
          postal_code:    form.postal_code.trim() || null,
          is_active:      form.is_active,
          email_verified: form.email_verified,
        }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acu-modal-overlay" onClick={onClose}>
      <div className="acu-modal acu-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="acu-modal__header">
          <div className="acu-modal__title-row">
            <span className="acu-modal__tag">EDIT</span>
            <h2 className="acu-modal__title">Edit Customer</h2>
          </div>
          <button className="acu-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="acu-modal__body acu-modal__body--form">
          {error && <div className="acu-modal__error">⚠ {error}</div>}

          {/* Personal Info */}
          <div className="acu-form-section">
            <h3 className="acu-form-section__title">Personal Information</h3>
            <div className="acu-form-grid acu-form-grid--2col">
              <div className="acu-form-field">
                <label>First Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  placeholder="e.g. John"
                />
              </div>
              <div className="acu-form-field">
                <label>Last Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)}
                  placeholder="e.g. Smith"
                />
              </div>
              <div className="acu-form-field">
                <label>Email Address <span className="req">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="e.g. john@example.com"
                />
              </div>
              <div className="acu-form-field">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="e.g. 071 234 5678"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="acu-form-section">
            <h3 className="acu-form-section__title">Shipping Address</h3>
            <div className="acu-form-grid acu-form-grid--2col">
              <div className="acu-form-field acu-form-field--full">
                <label>Street Address</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="e.g. 12 Main Road, Berea"
                />
              </div>
              <div className="acu-form-field">
                <label>City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="e.g. Durban"
                />
              </div>
              <div className="acu-form-field">
                <label>Province</label>
                <input
                  type="text"
                  value={form.province}
                  onChange={(e) => set("province", e.target.value)}
                  placeholder="e.g. KwaZulu-Natal"
                />
              </div>
              <div className="acu-form-field">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={form.postal_code}
                  onChange={(e) => set("postal_code", e.target.value)}
                  placeholder="e.g. 4001"
                />
              </div>
            </div>
          </div>

          {/* Account Options */}
          <div className="acu-form-section">
            <h3 className="acu-form-section__title">Account Options</h3>
            <div className="acu-form-checks">
              <label className="acu-check">
                <input
                  type="checkbox"
                  checked={Number(form.is_active) === 1}
                  onChange={(e) => set("is_active", e.target.checked ? 1 : 0)}
                />
                <span>Account Active (can log in)</span>
              </label>
              <label className="acu-check">
                <input
                  type="checkbox"
                  checked={Number(form.email_verified) === 1}
                  onChange={(e) => set("email_verified", e.target.checked ? 1 : 0)}
                />
                <span>Email Verified</span>
              </label>
            </div>
          </div>
        </div>

        <div className="acu-modal__footer">
          <button className="acu-btn acu-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="acu-btn acu-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   RESET PASSWORD MODAL
   ═══════════════════════════════════ */
function ResetPasswordModal({ customer, onClose, onSaved }) {
  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw,          setShowPw]          = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState("");

  const handleSubmit = async () => {
    setError("");
    if (!newPassword)                         return setError("New password is required.");
    if (newPassword.length < 8)               return setError("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword)      return setError("Passwords do not match.");

    setSaving(true);
    try {
      await apiFetch(`/api/admin/customers/${customer.id}/reset-password`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ new_password: newPassword }),
      });
      onSaved("Password reset successfully.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acu-modal-overlay" onClick={onClose}>
      <div className="acu-modal acu-modal--reset" onClick={(e) => e.stopPropagation()}>
        <div className="acu-modal__header">
          <div className="acu-modal__title-row">
            <span className="acu-modal__tag">SECURITY</span>
            <h2 className="acu-modal__title">Reset Password</h2>
          </div>
          <button className="acu-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="acu-reset__body">
          <div className="acu-reset__who">
            <div className="acu-avatar acu-avatar--sm">
              {initials(customer.first_name, customer.last_name)}
            </div>
            <div>
              <strong>{customer.first_name} {customer.last_name}</strong>
              <span>{customer.email}</span>
            </div>
          </div>

          {error && <div className="acu-modal__error">⚠ {error}</div>}

          <div className="acu-form-field">
            <label>New Password <span className="req">*</span></label>
            <div className="acu-pw-wrap">
              <input
                type={showPw ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                className="acu-pw-toggle"
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="acu-form-field">
            <label>Confirm New Password <span className="req">*</span></label>
            <div className="acu-pw-wrap">
              <input
                type={showPw ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
              />
            </div>
          </div>

          <p className="acu-reset__note">
            ⚠ This will immediately override the customer's existing password. They will need to use the new password to log in.
          </p>
        </div>

        <div className="acu-modal__footer">
          <button className="acu-btn acu-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="acu-btn acu-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ customer, onClose, onDeleted }) {
  const [confirm,  setConfirm]  = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const fullName   = `${customer?.first_name} ${customer?.last_name}`;
  const canDelete  = confirm.trim().toLowerCase() === "delete";

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/customers/${customer.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="acu-modal-overlay" onClick={onClose}>
      <div className="acu-modal acu-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="acu-delete__icon">🗑</div>
        <h2 className="acu-delete__title">Delete Customer?</h2>
        <p className="acu-delete__body">
          You are about to permanently delete <strong>{fullName}</strong> ({customer?.email}).
          Their order history will also be removed. This cannot be undone.
        </p>
        <div className="acu-delete__confirm-wrap">
          <label>Type <strong>delete</strong> to confirm</label>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="delete"
            className="acu-delete__confirm-input"
          />
        </div>
        {error && <div className="acu-modal__error">⚠ {error}</div>}
        <div className="acu-delete__actions">
          <button className="acu-btn acu-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            className="acu-btn acu-btn--delete"
            onClick={handleDelete}
            disabled={deleting || !canDelete}
          >
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminCustomers() {
  const [customers,    setCustomers]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [toast,        setToast]        = useState("");

  /* Filters + pagination */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [page,         setPage]         = useState(1);
  const [pagination,   setPagination]   = useState({ total: 0, pages: 1 });
  const LIMIT = 15;

  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null); // null | "view" | "edit" | "reset" | "delete"
  const [target, setTarget] = useState(null);

  /* ── Toast helper ── */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  /* ── Load ── */
  const loadCustomers = useCallback(() => {
    setLoading(true);
    setError("");

    const params = new URLSearchParams();
    if (search.trim())    params.set("search", search.trim());
    if (statusFilter)     params.set("active",   statusFilter);
    if (verifiedFilter)   params.set("verified", verifiedFilter);
    params.set("page",  page);
    params.set("limit", LIMIT);

    apiFetch(`/api/admin/customers?${params.toString()}`)
      .then((data) => {
        setCustomers(data.customers || []);
        setPagination(data.pagination || { total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, verifiedFilter, page]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  /* Reset page when filters change */
  useEffect(() => { setPage(1); }, [search, statusFilter, verifiedFilter]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 350);
    e.target.value = val; // keep input controlled
  };

  /* Toggle active via API directly (no modal) */
  const handleToggle = async (customer) => {
    try {
      const data = await apiFetch(`/api/admin/customers/${customer.id}/toggle`, { method: "PUT" });
      showToast(data.message || "Customer updated.");
      loadCustomers();
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal  = (type, customer = null) => { setModal(type); setTarget(customer); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = (msg = "Saved successfully.") => { closeModal(); showToast(msg); loadCustomers(); };
  const afterDelete = () => { closeModal(); showToast("Customer deleted."); loadCustomers(); };

  /* Stats */
  const totalActive   = pagination.total; // full total from server
  const shownCount    = customers.length;

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Customers">

      {/* Toast */}
      {toast && (
        <div className="acu-toast">{toast}</div>
      )}

      {error && <div className="acu-error-bar">⚠ {error}</div>}

      {/* ── Summary strip ── */}
      <div className="acu-summary">
        <div className="acu-summary__item">
          <span className="acu-summary__value">{loading ? "…" : pagination.total}</span>
          <span className="acu-summary__label">Total</span>
        </div>
        <div className="acu-summary__divider" />
        <div className="acu-summary__item">
          <span className="acu-summary__value">{loading ? "…" : shownCount}</span>
          <span className="acu-summary__label">Shown</span>
        </div>
        <div className="acu-summary__divider" />
        <div className="acu-summary__item">
          <span className="acu-summary__value">{loading ? "…" : pagination.pages}</span>
          <span className="acu-summary__label">Pages</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="acu-toolbar">
        <div className="acu-toolbar__left">
          <div className="acu-search-wrap">
            <span className="acu-search-wrap__icon">🔍</span>
            <input
              className="acu-search"
              type="text"
              placeholder="Search name, email, phone…"
              defaultValue={search}
              onChange={handleSearchChange}
            />
          </div>

          <select
            className="acu-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="acu-filter-select"
            value={verifiedFilter}
            onChange={(e) => setVerifiedFilter(e.target.value)}
          >
            <option value="">All Verification</option>
            <option value="1">Verified</option>
            <option value="0">Unverified</option>
          </select>
        </div>

        <div className="acu-toolbar__right">
          <span className="acu-toolbar__count">
            {loading ? "…" : `${pagination.total} customer${pagination.total !== 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="acu-table-skeleton" />
      ) : customers.length === 0 ? (
        <div className="acu-empty">
          <span>👤</span>
          <p>No customers found.</p>
          {(search || statusFilter || verifiedFilter) && (
            <button
              className="acu-btn acu-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setVerifiedFilter(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="acu-table-wrap">
          <table className="acu-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Verified</th>
                <th>Last Login</th>
                <th>Registered</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} onDoubleClick={() => openModal("view", c)} title="Double-click to view">
                  <td className="acu-table__id">{c.id}</td>
                  <td>
                    <div className="acu-name-cell">
                      <div className="acu-avatar acu-avatar--xs">
                        {initials(c.first_name, c.last_name)}
                      </div>
                      <div className="acu-name-cell__text">
                        <span className="acu-name-cell__name">
                          {c.first_name} {c.last_name}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <a href={`mailto:${c.email}`} className="acu-email-link">{c.email}</a>
                  </td>
                  <td>
                    {c.phone
                      ? <a href={`tel:${c.phone}`} className="acu-phone-link">{c.phone}</a>
                      : <span className="acu-muted">—</span>}
                  </td>
                  <td>
                    <span className="acu-location">
                      {[c.city, c.province].filter(Boolean).join(", ") || <span className="acu-muted">—</span>}
                    </span>
                  </td>
                  <td><VerifiedBadge verified={c.email_verified} /></td>
                  <td>
                    <span className="acu-date">{fmtDateShort(c.last_login)}</span>
                  </td>
                  <td>
                    <span className="acu-date">{fmtDateShort(c.created_at)}</span>
                  </td>
                  <td><ActiveBadge active={c.is_active} /></td>
                  <td>
                    <DotMenu
                      isActive={Number(c.is_active) === 1}
                      onView={()     => openModal("view",   c)}
                      onEdit={()     => openModal("edit",   c)}
                      onResetPw={() => openModal("reset",  c)}
                      onToggle={() =>  handleToggle(c)}
                      onDelete={() => openModal("delete", c)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && pagination.pages > 1 && (
        <div className="acu-pagination">
          <button
            className="acu-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>

          <div className="acu-page-numbers">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === pagination.pages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "…" ? (
                  <span key={`ellipsis-${idx}`} className="acu-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`acu-page-btn acu-page-btn--num ${p === page ? "acu-page-btn--active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
          </div>

          <button
            className="acu-page-btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view" && (
        <ViewModal
          customer={target}
          onClose={closeModal}
          onEdit={() => openModal("edit", target)}
        />
      )}
      {modal === "edit" && (
        <EditModal customer={target} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "reset" && (
        <ResetPasswordModal customer={target} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "delete" && (
        <DeleteModal customer={target} onClose={closeModal} onDeleted={afterDelete} />
      )}

    </AdminLayout>
  );
}